import { capitalize } from "utils/capitalize";
import { formatDate } from "utils/formatDate";
import { formatProtocol } from "utils/formatProtocol";
import { formatRegion } from "utils/formatRegion";
import { DATE_FORMATS } from "../../constants";
import { RootState } from "../../reducers";
import {
  Firewall,
  FirewallRule,
  FloatingIP,
  Network,
  NetworkLimits,
  Router,
  Subnet,
  TableFirewallRule,
  TableFloatingIP,
  TableNetworkInterface,
  TableRouter
} from "./types";
import { createSelector } from "reselect";

const getNetworks = (state: RootState) => state.networks.networks;
export const tableNetworksSelector = createSelector(
  [getNetworks],
  (networks) => {
    return (
      networks?.map((network) => ({
        id: network.id,
        name: network.name,
        region: formatRegion(network.region),
        shared: network.shared,
        status: network.status,
        updatedAt: formatDate(
          new Date(network.updated_at),
          DATE_FORMATS.DATETIME
        ),
        description: network.description
      })) || null
    );
  }
);

// export const tableNetworksSelector = (
//   state: RootState
// ): TableNetwork[] | null =>
//   state.networks.networks?.map((network) => ({
//     id: network.id,
//     name: network.name,
//     region: formatRegion(network.region),
//     shared: network.shared,
//     status: network.status,
//     updatedAt: formatDate(new Date(network.updated_at), DATE_FORMATS.DATETIME),
//     description: network.description
//   })) || null;

export const networkSelector = (state: RootState): Network | null =>
  state.networks.network;

export const floatingIPsSelector = (state: RootState): FloatingIP[] | null =>
  state.networks.floatingIPs;

// export const tableFloatingIPsSelector = (
//   state: RootState
// ): TableFloatingIP[] | null =>
//   state.networks.floatingIPs?.map((floatingIP) => ({
//     id: floatingIP.id,
//     ipAddress: floatingIP.floating_ip_address,
//     fixedIPAddress: floatingIP.port_ip_address,
//     descriptions: floatingIP.description,
//     status: floatingIP.status
//   })) || null;

export const tableFloatingIPsSelector = createSelector(
  [floatingIPsSelector],
  (floatingIPs): TableFloatingIP[] | null =>
    floatingIPs
      ? floatingIPs.map((floatingIP) => ({
          id: floatingIP.id,
          ipAddress: floatingIP.floating_ip_address,
          fixedIPAddress: floatingIP.port_ip_address,
          descriptions: floatingIP.description,
          status: floatingIP.status
        }))
      : null
);

export const areFloatingIPsLoadingSelector = (state: RootState): boolean =>
  state.networks.areFloatingIPsLoading;

export const isFloatingIPCreating = (state: RootState): boolean =>
  state.networks.isFloatingIPCreating;

export const isFloatingIPUpdatingSelector = (state: RootState): boolean =>
  state.networks.isFloatingIPUpdating;

export const isFloatingIPDeletingSelector = (state: RootState): boolean =>
  state.networks.isFloatingIPDeleting;

export const routerSelector = (state: RootState): Router | null =>
  state.networks.router;

export const networkSubnetsSelector = (state: RootState): Subnet[] | null =>
  state.networks.networkSubnets;

export const subnetsSelector = (state: RootState): Subnet[] | null =>
  state.networks.subnets;

export const isFirewallLoadingSelector = (state: RootState): boolean =>
  state.networks.isFirewallLoading;

export const isFirewallCreatingSelector = (state: RootState): boolean =>
  state.networks.isFirewallCreating;

export const isFirewallUpdatingSelector = (state: RootState): boolean =>
  state.networks.isFirewallUpdating;

export const isFirewallDeletingSelector = (state: RootState): boolean =>
  state.networks.isFirewallDeleting;

export const firewallsSelector = (state: RootState): Firewall[] | null =>
  state.networks.firewalls;

export const tableFirewallsSelector = createSelector(
  [firewallsSelector],
  (firewalls) => {
    return (
      firewalls?.map((firewall) => ({
        name: firewall.name,
        id: firewall.id,
        description: firewall.description
      })) || null
    );
  }
);
// export const tableFirewallsSelector = (
//   state: RootState
// ): TableFirewall[] | null =>
//   state.networks.firewalls?.map((firewall) => ({
//     name: firewall.name,
//     id: firewall.id,
//     description: firewall.description
//   })) || null;

export const firewallSelector = (state: RootState): Firewall | null =>
  state.networks.firewall;

export const firewallRuleSelector = (state: RootState): FirewallRule | null =>
  state.networks.firewallRule;

export const tableFirewallRulesSelector = (
  state: RootState
): TableFirewallRule[] | null =>
  state.networks.firewallRules?.map((firewallRule) => ({
    id: firewallRule.id,
    direction: capitalize(firewallRule.direction),
    protocol: formatProtocol(firewallRule.protocol),
    ipVersion: firewallRule.ethertype,
    description: firewallRule.description,
    portRange: firewallRule.port_range,
    ipRanges: firewallRule.remote_ip_prefix,
    remoteFirewall:
      state.networks.firewalls?.find(
        (firewall) => firewall.id === firewallRule.remote_group_id
      )?.name || ""
  })) || null;

export const isNetworkCreatingSelector = (state: RootState): boolean =>
  state.networks.isNetworkCreating;

export const isNetworkUpdatingSelector = (state: RootState): boolean =>
  state.networks.isNetworkUpdating;

export const isNetworkDeletingSelector = (state: RootState): boolean =>
  state.networks.isNetworkDeleting;

export const isNetworkLoadingSelector = (state: RootState): boolean =>
  state.networks.isNetworkLoading;

export const areNetworksLoadingSelector = (state: RootState): boolean =>
  state.networks.areNetworksLoading;

export const networkLimitsSelector = (state: RootState): NetworkLimits | null =>
  state.networks.networkLimits;

export const areNetworkLimitsLoadingSelector = (state: RootState): boolean =>
  state.networks.areNetworkLimitsLoading;

export const areSubnetsLoadingSelector = (state: RootState): boolean =>
  state.networks.areSubnetsLoading;

export const areNetworkSubnetsLoadingSelector = (state: RootState): boolean =>
  state.networks.areNetworkSubnetsLoading;

export const isSubnetCreatingSelector = (state: RootState): boolean =>
  state.networks.isSubnetCreating;

export const isSubnetUpdatingSelector = (state: RootState): boolean =>
  state.networks.isSubnetUpdating;

export const isSubnetDeletingSelector = (state: RootState): boolean =>
  state.networks.isSubnetDeleting;

export const areFirewallsLoadingSelector = (state: RootState): boolean =>
  state.networks.areFirewallsLoading;

export const areFirewallRulesLoadingSelector = (state: RootState): boolean =>
  state.networks.areFirewallRulesLoading;

export const isFirewallRuleLoadingSelector = (state: RootState): boolean =>
  state.networks.isFirewallRuleLoading;

export const isFirewallRuleCreatingSelector = (state: RootState): boolean =>
  state.networks.isFirewallRuleCreating;

export const isFirewallRuleDeletingSelector = (state: RootState): boolean =>
  state.networks.isFirewallRuleDeleting;

export const isRouterLoadingSelector = (state: RootState): boolean =>
  state.networks.isRouterLoading;

export const isRouterCreatingSelector = (state: RootState): boolean =>
  state.networks.isRouterCreating;

export const isRouterUpdatingSelector = (state: RootState): boolean =>
  state.networks.isRouterUpdating;

export const isRouterStateUpdatingSelector = (state: RootState): boolean =>
  state.networks.isRouterStateUpdating;

export const isRouterDeletingSelector = (state: RootState): boolean =>
  state.networks.isRouterDeleting;

export const areFloatingIPLoadingSelector = (state: RootState): boolean =>
  state.networks.areFloatingIPsLoading;

export const isFloatingIPCreatingSelector = (state: RootState): boolean =>
  state.networks.isFloatingIPCreating;

export const isRouterInterfaceAddingSelector = (state: RootState): boolean =>
  state.networks.isRouterInterfaceAdding;

export const isRouterInterfaceDeletingSelector = (state: RootState): boolean =>
  state.networks.isRouterInterfaceDeleting;

export const tableRoutersSelector = (state: RootState): TableRouter[] | null =>
  state.networks.routers?.map((router) => ({
    id: router.id,
    name: router.name,
    status: router.status,
    externalGateway: router.external_gateway_info.external_fixed_ips
      ? "Enabled"
      : "",
    state: router.admin_state_up ? "Up" : "Down",
    description: router.description
  })) || null;

// export const interfacesSelector = (state: RootState): Interface[] | null =>
//   state.networks.interfaces?.map((routerInterface) => ({
//     id: routerInterface.id,
//     status: routerInterface.status,
//     admin_state_up: routerInterface.admin_state_up,
//     mac_address: routerInterface.mac_address,
//     fixed_ips: routerInterface.fixed_ips,
//     network_id: routerInterface.network_id,
//     security_groups: routerInterface.security_groups,
//     device_id: routerInterface.device_id
//   })) || null;

const getInterfaces = (state: RootState) => state.networks.interfaces;
export const interfacesSelector = createSelector(
  [getInterfaces],
  (interfaces): TableNetworkInterface[] | null => {
    return (
      interfaces?.map((i) => ({
        id: i.id,
        name: i.name,
        display_name: i.name ? i.name : `(${i.id.slice(0, 13)})`,
        status: i.status,
        admin_state_up: i.admin_state_up,
        admin_state_string: i.admin_state_up ? "up" : "down",
        mac_address: i.mac_address,
        fixed_ips: i.fixed_ips,
        ips:
          i.fixed_ips
            ?.map((ip) => ip.ip_address)
            .filter(Boolean)
            .join(", ") || "",
        network_id: i.network_id,
        firewalls: i.security_groups,
        firewalls_string: i.security_groups?.join(", ") || "",
        device_id: i.device_id
      })) || null
    );
  }
);

// export const tableInterfacesSelector = (
//   state: RootState
// ): TableInterface[] | null =>
//   state.networks.routerInterfaces?.map((routerInterface) => ({
//     id: routerInterface.id,
//     status: routerInterface.status,
//     admin_state_up: routerInterface.admin_state_up,
//     ip: routerInterface.fixed_ips
//       ? routerInterface.fixed_ips[0].ip_address
//       : "",
//     subnetId: routerInterface.fixed_ips
//       ? routerInterface.fixed_ips[0].subnet_id
//       : ""
//   })) || null;

const getRouterInterfaces = (state: RootState) =>
  state.networks.routerInterfaces;

export const tableRouterInterfacesSelector = createSelector(
  [getRouterInterfaces],
  (routerInterfaces): TableNetworkInterface[] | null => {
    return (
      routerInterfaces?.map((routerInterface) => ({
        id: routerInterface.id,
        name: routerInterface.name,
        status: routerInterface.status,
        admin_state_up: routerInterface.admin_state_up,
        admin_state_string: routerInterface.admin_state_up ? "up" : "down",
        device_id: routerInterface.device_id,
        device_owner: routerInterface.device_owner || "",
        mac_address: routerInterface.mac_address,
        firewalls: routerInterface.security_groups,
        firewalls_string: routerInterface.security_groups?.join(", ") || "",
        fixed_ips: routerInterface.fixed_ips,
        ips:
          routerInterface.fixed_ips
            ?.map((ip) => ip.ip_address)
            .filter(Boolean)
            .join(", ") || "",
        subnet_ids:
          routerInterface.fixed_ips
            ?.map((ip) => ip.subnet_id)
            .filter(Boolean)
            .join(", ") || ""
      })) || null
    );
  }
);

export const isNetworkInterfaceDeletingSelector = (state: RootState): boolean =>
  state.networks.isNetworkInterfaceDeleting;

export const isNetworkInterfaceEditingSelector = (state: RootState): boolean =>
  state.networks.isNetworkInterfaceEditing;

export const isNetworkInterfaceCreatingSelector = (state: RootState): boolean =>
  state.networks.isNetworkInterfaceCreating;

export const areNetworkInterfacesLoadingSelector = (
  state: RootState
): boolean => state.networks.areNetworkInterfacesLoading;

const getNetworkInterfaces = (state: RootState) =>
  state.networks.networkInterfaces;

export const tableNetworkInterfacesSelector = createSelector(
  [getNetworkInterfaces, networkSubnetsSelector],
  (networkInterfaces, networkSubnets): TableNetworkInterface[] | null => {
    return (
      networkInterfaces?.map((networkInterface) => ({
        id: networkInterface.id,
        name: networkInterface.name,
        display_name: networkInterface.name
          ? networkInterface.name
          : `(${networkInterface.id.slice(0, 13)})`,
        status: networkInterface.status,
        admin_state_up: networkInterface.admin_state_up,
        admin_state_string: networkInterface.admin_state_up ? "up" : "down",
        port_security_enabled: networkInterface.port_security_enabled,
        port_security_enabled_string: networkInterface.port_security_enabled
          ? "enabled"
          : "disabled",
        device_id: networkInterface.device_id,
        device_owner: networkInterface.device_owner || "",
        mac_address: networkInterface.mac_address,
        firewalls: networkInterface.security_groups,
        firewalls_string: networkInterface.security_groups?.join(", ") || "",
        fixed_ips: networkInterface.fixed_ips,
        ips:
          networkInterface.fixed_ips
            ?.map((ip) => ip.ip_address)
            .filter(Boolean)
            .join(", ") || "",
        subnet_ids:
          networkInterface.fixed_ips
            ?.map((ip) => ip.subnet_id)
            .filter(Boolean)
            .join(", ") || "",
        subnet_names:
          networkInterface.fixed_ips
            ?.map(
              (ip) => networkSubnets?.find((s) => s.id === ip.subnet_id)?.name
            )
            .filter(Boolean)
            .join(", ") || "",
        allowed_address_pairs: networkInterface.allowed_address_pairs,
        addressPairsString:
          networkInterface.allowed_address_pairs
            ?.map((pair) => pair.ip_address)
            .filter(Boolean)
            .join(", ") || ""
      })) || null
    );
  }
);
